import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { TalkEffect } from "./ToolboxTab";
import { Fire, ChalkboardTeacher, Sparkle } from "@phosphor-icons/react";

const slug = (s) => String(s || "").replace(/[^A-Za-z0-9_-]/g, "_");

/** Tap a hotspot → the AI drafts a toolbox talk on that site and cause, saved as a draft and opened to review. */
function HotspotTalkButton({ x, testid }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const draft = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/hs/toolbox-talks/ai-draft", { site: x.site_label, cause: x.cause });
      toast.success(data.message);
      nav(`/health-safety?tab=toolbox&talk=${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't draft the toolbox talk"); } finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={busy} onClick={draft} title="AI drafts a 10-minute toolbox talk on this site and cause for you to review and deliver" data-testid={testid}>
      {busy ? <Sparkle size={11} className="mr-1 animate-pulse" /> : <ChalkboardTeacher size={11} className="mr-1" />}{busy ? "Drafting…" : "Draft toolbox talk"}
    </Button>
  );
}

function cellStyle(cell, max) {
  if (!cell || !cell.total) return { background: "transparent" };
  const t = Math.max(0.15, cell.total / Math.max(1, max));
  const accidentShare = cell.accident / cell.total;
  // amber for near-miss-heavy cells, red as accidents dominate
  const hue = Math.round(38 - 38 * accidentShare);
  return { background: `hsla(${hue}, 92%, 48%, ${0.18 + 0.72 * t})`, color: t > 0.55 ? "#fff" : undefined };
}

/** Site × cause heat map over the last 12 months — which sites and causes keep coming up, so toolbox talks can be targeted. */
export function IncidentHeatMap({ heatmap }) {
  const h = heatmap || {};
  const sites = h.sites || [];
  const causes = h.causes || [];
  if (!sites.length || !causes.length) return <p className="text-xs text-muted-foreground" data-testid="inc-heat-empty">The heat map fills in once incidents are logged with a cause and a site.</p>;
  const lookup = {};
  for (const c of h.cells || []) lookup[`${c.site}|${c.cause}`] = c;
  return (
    <div className="space-y-3" data-testid="inc-heatmap">
      <div className="overflow-x-auto">
        <table className="text-xs border-separate border-spacing-0.5" data-testid="inc-heat-table">
          <thead>
            <tr>
              <th className="text-left font-semibold text-muted-foreground pr-2 pb-1 align-bottom">Site</th>
              {causes.map((c) => <th key={c.key} className="font-medium text-muted-foreground pb-1 align-bottom max-w-[88px]" data-testid={`inc-heat-cause-${c.key}`}><div className="text-[10px] leading-tight whitespace-normal">{c.label}</div><div className="text-[9px] font-normal">{c.total}</div></th>)}
              <th className="text-right font-semibold text-muted-foreground pl-2 pb-1 align-bottom">Total</th>
            </tr>
          </thead>
          <tbody>
            {sites.map((s) => (
              <tr key={s.key} data-testid={`inc-heat-site-${slug(s.key)}`}>
                <td className="pr-2 py-0.5 whitespace-nowrap max-w-[200px] truncate" title={s.client_name ? `${s.label} · ${s.client_name}` : s.label}><span className="font-medium">{s.label}</span>{s.client_name && <span className="text-muted-foreground"> · {s.client_name}</span>}</td>
                {causes.map((c) => {
                  const cell = lookup[`${s.key}|${c.key}`];
                  return (
                    <td key={c.key} className="text-center font-semibold rounded-sm h-8 min-w-[52px]" style={cellStyle(cell, h.max)} title={cell ? `${s.label} · ${c.label}: ${cell.total} incident${cell.total === 1 ? "" : "s"} (${cell.accident} accident${cell.accident === 1 ? "" : "s"}, ${cell.near_miss} near miss${cell.near_miss === 1 ? "" : "es"})` : `${s.label} · ${c.label}: none`} data-testid={`inc-heat-cell-${slug(s.key)}-${c.key}`}>
                      {cell?.total || <span className="text-muted-foreground/40">·</span>}
                    </td>
                  );
                })}
                <td className="text-right pl-2 font-semibold" data-testid={`inc-heat-total-${slug(s.key)}`}>{s.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm" style={{ background: "hsla(38, 92%, 48%, .8)" }} /> Mostly near misses</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm" style={{ background: "hsla(0, 92%, 48%, .8)" }} /> Mostly accidents</span>
        <span>Darker = more incidents · last 12 months · top 8 sites and causes</span>
      </div>
      {(h.hotspots || []).length > 0 && (
        <div data-testid="inc-hotspots">
          <h4 className="text-xs font-semibold mb-1 flex items-center gap-1"><Fire size={13} weight="fill" className="text-red-600" /> Hotspots to target with a toolbox talk</h4>
          <ul className="space-y-1.5">
            {h.hotspots.map((x, i) => (
              <li key={`${x.site}-${x.cause}`} className="text-xs" data-testid={`inc-hotspot-${i}`}>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-medium">{x.site_label}</span><span className="text-muted-foreground">·</span><span>{x.cause_label}</span>
                  <span className="text-muted-foreground">— {x.total} incident{x.total === 1 ? "" : "s"}{x.accident ? ` (${x.accident} accident${x.accident === 1 ? "" : "s"})` : ""}</span>
                  <HotspotTalkButton x={x} testid={`inc-hotspot-talk-${i}`} />
                </div>
                {x.talk && (
                  <div className="ml-3 mt-0.5 flex flex-wrap items-center gap-x-2" data-testid={`inc-hotspot-effect-${i}`}>
                    <Link to={`/health-safety?tab=toolbox&talk=${x.talk.id}`} className="text-[11px] text-primary hover:underline inline-flex items-center gap-1" data-testid={`inc-hotspot-talk-link-${i}`}><ChalkboardTeacher size={11} /> Talk delivered {new Date(x.talk.delivered_on).toLocaleDateString("en-GB")}</Link>
                    <TalkEffect e={x.talk} />
                  </div>
                )}
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-muted-foreground mt-1">The talk is saved as a draft with no attendees — review the wording, then deliver it to the team from the Toolbox talks tab.</p>
        </div>
      )}
    </div>
  );
}
